/**
 * Constants for the Operating Points page
 */

/**
 * Builds a stable column key for Operating Points (merge, saved views, measurement sort/UI).
 *
 * - Finite `universalDescriptorId` → `{label}|ud:{id}` (tagName ignored).
 * - Otherwise → `{label}|{tagName}` (both trimmed).
 *
 * Asset columns: pass label + tagName only. UD measurement columns: pass label + UD; use `tagName` "" on the column object.
 *
 * @param {string} labelName
 * @param {string} [tagName]
 * @param {number|string|null|undefined} [universalDescriptorId]
 */
export function makeColumnKey(labelName, tagName, universalDescriptorId) {
  const l = String(labelName ?? "").trim();
  const rawUd = universalDescriptorId;
  if (rawUd != null && rawUd !== "") {
    const ud = Number(rawUd);
    if (Number.isFinite(ud)) {
      return `${l}|ud:${ud}`;
    }
  }
  const t = String(tagName ?? "").trim();
  return `${l}|${t}`;
}

// Default tabs for the operating points comparison
export const DEFAULT_TABS = [
  { id: "pressure", label: "Pressure Comparison" },
  { id: "temperature", label: "Engine Temperatures" },
  { id: "spark", label: "Cylinder Spark KV" },
];

// Asset property names that are displayed as fixed columns
export const ASSET_PROPERTY_NAMES = new Set([
  "assetNumber",
  "lease",
  "status",
  "lastUpdated",
]);

// Default columns that are always shown (asset properties)
export const DEFAULT_COLUMNS = [
  {
    key: makeColumnKey("Asset Number", "assetNumber"),
    labelName: "Asset Number",
    tagName: "assetNumber",
  },
  {
    key: makeColumnKey("Lease Name", "lease"),
    labelName: "Lease Name",
    tagName: "lease",
  },
  {
    key: makeColumnKey("Status", "status"),
    labelName: "Status",
    tagName: "status",
  },
  {
    key: makeColumnKey("Last Report", "lastUpdated"),
    labelName: "Last Report",
    tagName: "lastUpdated",
  },
];

const createColumn = (labelName, universalDescriptorId, unit) => ({
  key: makeColumnKey(labelName, "", universalDescriptorId),
  labelName,
  unit,
  tagName: "",
  universalDescriptorId,
});

// Measurement column definitions for each tab
export const MEASUREMENT_COLUMNS = {
  pressure: [
    createColumn("Suction Pressure", 1176, "PSI"),
    createColumn("Discharge Pressure", 612, "PSI"),
    createColumn("Stage 1 Discharge Pressure", 683, "PSI"),
  ],
  temperature: [
    createColumn("ENGINE JACKET WATER TEMPERATURE", 255, "°F"),
    createColumn("Engine Oil Pressure", 797, "PSI"),
    createColumn("ENGINE RPM", 627, "RPM"),
  ],
  spark: [
    createColumn("SPARK KV CYLINDER 1", 569, "KV"),
    createColumn("SPARK KV CYLINDER 2", 968, "KV"),
    createColumn("SPARK KV CYLINDER 3", 980, "KV"),
    createColumn("SPARK KV CYLINDER 4", 582, "KV"),
  ],
};
